/**
 * Billing portal URLs and pricing utilities for Stripe account migration
 *
 * User cohorts:
 *   Cohort A — OLD Stripe account (grandfathered at $20/$50)
 *   Cohort B — NEW Stripe account, legacy prices (grandfathered at $75/$150)
 *   Cohort C legacy  — NEW Stripe account, prior prices (grandfathered at $49/$99/$179)
 *   Cohort C doubled — NEW Stripe account, 2026-05 prices (grandfathered at $99/$199/$349)
 *   Cohort D — NEW Stripe account, 2026-07-15 prices (grandfathered at $20/$50/$150)
 *   Cohort E current — NEW Stripe account, 2026-07-16 repricing ($50/$100/$200)
 *
 * The pricing page always shows current Cohort E prices for new checkouts.
 * Existing subscribers stay on whatever price they signed up with and manage
 * their plan via the Stripe billing portal.
 */

export const BILLING_PORTALS = {
  OLD: "https://billing.stripe.com/p/login/8wM7usdvg5aq1tCfYY",
  NEW: "https://billing.stripe.com/p/login/7sYaEY5yee4c1XSeKBg7e00",
} as const;

export const PLAN_PRICES = {
  OLD: {
    plus: { monthly: '20' },
    pro: { monthly: '50' },
  },
  NEW: {
    plus: {
      monthly: '50',
      annual: '500',
      annual_per_month: '42',
    },
    pro: {
      monthly: '100',
      annual: '1,000',
      annual_per_month: '83',
    },
    elite: {
      monthly: '200',
      annual: '2,000',
      annual_per_month: '167',
    },
  },
} as const;

/**
 * Get the appropriate billing portal URL based on stripe account type
 * @param stripeAccount - "new", "old", or null (defaults to old for backward compatibility)
 * @returns Billing portal URL
 */
export function getBillingPortalUrl(stripeAccount?: "new" | "old" | null): string {
  return stripeAccount === "new" ? BILLING_PORTALS.NEW : BILLING_PORTALS.OLD;
}

/**
 * Get display prices for plans based on stripe account type and billing interval
 * @param stripeAccount - "new", "old", or null (defaults to old for backward compatibility)
 * @param billingInterval - "month" or "year" (only applicable for new accounts)
 * @returns Object with plus, pro, and elite prices
 */
export function getDisplayPrices(
  stripeAccount?: "new" | "old" | null,
  billingInterval: 'month' | 'year' = 'month'
) {
  const isNew = stripeAccount === "new";

  if (!isNew) {
    // Old accounts only have monthly pricing, no elite
    const oldPrices = PLAN_PRICES.OLD;
    return {
      plus: oldPrices.plus.monthly,
      pro: oldPrices.pro.monthly,
      elite: null,
    };
  }

  // New accounts have monthly and annual pricing
  const newPrices = PLAN_PRICES.NEW;
  if (billingInterval === 'year') {
    return {
      plus: newPrices.plus.annual,
      pro: newPrices.pro.annual,
      elite: newPrices.elite.annual,
      plus_per_month: newPrices.plus.annual_per_month,
      pro_per_month: newPrices.pro.annual_per_month,
      elite_per_month: newPrices.elite.annual_per_month,
    };
  }

  return {
    plus: newPrices.plus.monthly,
    pro: newPrices.pro.monthly,
    elite: newPrices.elite.monthly,
  };
}
